import { formatMoney } from '@theme/money-formatting';

/**
 * Web Component for the add-to-cart button with inline price display.
 * Listens for selling-plan-picker:price-change and variant:update events
 * to keep the displayed price in sync with the selected options.
 */
class NcnAddToCartPrice extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;
  /** @type {boolean} Whether a selling-plan-picker exists in this section */
  #hasPicker = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    this.#hasPicker = !!container?.querySelector('selling-plan-picker');

    // Listen for price changes from selling-plan-picker — the single source of
    // truth for pricing (accounts for bulk discounts, quantity, and subscriptions).
    // The selling-plan-picker already handles variant:update internally and
    // dispatches this event with the correct total, so we don't listen to
    // variant:update here to avoid overwriting the discounted price.
    container?.addEventListener('selling-plan-picker:price-change', (event) => {
      this.#updatePrice(event.detail.totalPrice);
    }, { signal });

    // Enable/disable the submit button when variant availability changes.
    // Also update the price directly when no selling-plan-picker is present,
    // since the price-change event won't fire in that case.
    document.addEventListener('variant:update', (event) => {
      if (event.detail?.data?.productId !== this.dataset.productId) return;
      const button = this.querySelector('button[name="add"]');
      if (!button) return;

      const variant = event.detail.resource;
      const available = !!variant?.available;
      button.disabled = !available;

      // Show/hide the price display when availability changes
      const display = this.querySelector('[ref="priceDisplay"]');
      if (display) display.hidden = !available;

      if (!this.#hasPicker && variant?.price != null) {
        this.#updatePrice(variant.price);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Updates the price display element with a formatted price.
   * @param {number} priceInMinorUnits - Price in minor currency units (e.g., cents)
   */
  #updatePrice(priceInMinorUnits) {
    const display = this.querySelector('[ref="priceDisplay"]');
    if (!display) return;

    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (!format || !currency) return;

    display.textContent = formatMoney(priceInMinorUnits, format, currency);
  }
}

if (!customElements.get('ncn-add-to-cart-price')) {
  customElements.define('ncn-add-to-cart-price', NcnAddToCartPrice);
}
